import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// Interfaces para las APIs
export interface CreatePackageListRequest {
  name: string;
  type: 'empaquetado' | 'enviado' | 'cargado';
  transportType?: 'camion' | 'camioneta';
  createdBy?: string;
}

export interface AddPackageListItemRequest {
  packageListId: number;
  sparePartId: number;
  sparePartName: string;
  sparePartNameEs?: string;  // Nombre en español
  sparePartNamePt?: string;  // Nombre en portugués
  productCode: string;
  packageNumber?: string;
  orderNumber?: string;
  remitoNumber?: string;
  itemStatus?: string;
  workshopId?: number;
}

export interface UpdatePackageListItemStatusRequest {
  packageListItemId: number;
  itemStatus: string;
  remitoNumber?: string;
}

export interface CompletePackageListRequest {
  packageListId: number;
  notes?: string;
}

export interface ScanPackageRequest {
  packageNumber: string;
  scannedBy?: string;
}

export interface ScanPackageResponse {
  success: boolean;
  message: string;
  itemsAdded: number;
}

export interface ProcessPackageRequest {
  packageNumber: string;
  newStatus: string;
  processedBy?: string;
}

export interface ProcessPackageResponse {
  success: boolean;
  message: string;
  updatedCount: number;
}

export interface ValidatePackageForEnvioResponse {
  isValid: boolean;
  message: string;
  packageExists: boolean;
  isInCargadoState: boolean;
}

export interface ValidatePackageForCargadoResponse {
  isValid: boolean;
  message: string;
  packageExists: boolean;
  belongsToEmpaquetadoList: boolean;
}

export interface PackageListOutModel {
  packageListId: number;
  name: string;
  type: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  transportType?: string;
  createdBy?: string;
  items: PackageListItemOutModel[];
  totalItems: number;
  checkedItems: number;
  toLoadItems: number;
}

export interface PackageListItemOutModel {
  packageListItemId: number;
  packageListId: number;
  sparePartId: number;
  sparePartName: string;
  sparePartNameEs?: string;  // Nombre en español
  sparePartNamePt?: string;  // Nombre en portugués
  productCode: string;
  packageNumber?: string;
  orderNumber?: string;
  scannedAt: string;
  itemStatus: string;
  remitoNumber?: string;
  position: number;
  workshopId?: number;
}

export interface PackageListSearchRequest {
  type?: string;
  status?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  createdBy?: string;
  page?: number;
  pageSize?: number;
}

export interface PackageListPagedResponse {
  lists: PackageListOutModel[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PackageListStatsResponse {
  totalLists: number;
  activeLists: number;
  completedLists: number;
  totalItems: number;
  listsByType: Record<string, number>;
  itemsByStatus: Record<string, number>;
}

export interface UniquePackageModel {
  packageNumber: string;
  sparePartsCount: number;
  totalAmount: number;
  orderNumber: string;
  siniestro?: string; // ✅ NUEVO: número de siniestro de la orden
  createdAt?: string | null;
  /** Max(LastExportedAt) across spare parts in the package (camión or camioneta load date). */
  loadedToTruckAt?: string | null;
  sentAt?: string;
  listType: string;
  packageListId?: number; // Opcional, ya que el backend no lo retorna
  packageListName?: string; // Opcional
  transportType?: string; // Tipo de vehículo: camion o camioneta
  /** Ítems de la PackageList. El historial arma los íconos sin pedir cada lista. */
  sparePartIds?: number[];
}

// Interfaces para gestión de paquetes existentes
export interface AvailableSparePartModel {
  sparePartId: number;
  sparePartName: string;
  productCode: string;
  amount: number;
  unitPrice: number;
  productStatus: string;
  orderNumber: string;
}

export interface EmpaquetadoParaCargadoModel {
  sparePartId: number;
  sparePartName: string;
  sparePartNameEs?: string;  // Nombre en español
  sparePartNamePt?: string;  // Nombre en portugués
  productCode: string;
  packageNumber: string;
  productStatus: string;
  orderNumber: string;
  transportType?: string;  // Tipo de vehículo (camion/camioneta)
}

export interface AddSparePartToPackageResponse {
  success: boolean;
  message: string;
  sparePartId: number;
  packageNumber: string;
}

export interface RemoveSparePartFromPackageResponse {
  success: boolean;
  message: string;
  sparePartId: number;
  packageNumber: string;
  shouldDeletePackage: boolean;
  remainingSpareParts: number;
}

export interface DeleteEmptyPackageResponse {
  success: boolean;
  message: string;
  packageNumber: string;
}

export interface CheckDuplicateItemRequest {
  sparePartId: number;
  productCode?: string;
  packageNumber?: string;
}

export interface CheckDuplicateItemResponse {
  isDuplicate: boolean;
  existingItemPosition?: string;
  message?: string;
  existingItem?: PackageListItemOutModel;
}

@Injectable({
  providedIn: 'root'
})
export class PackageListsService {
  private apiUrl = `${environment.apiEndpoint}/PackageList`;

  constructor(private http: HttpClient) { }

  // === GESTIÓN DE LISTAS ===

  /**
   * Obtener todas las listas con filtros opcionales
   */
  getPackageLists(searchRequest?: PackageListSearchRequest): Observable<PackageListPagedResponse> {
    let params = new HttpParams();
    
    if (searchRequest) {
      if (searchRequest.type) params = params.set('type', searchRequest.type);
      if (searchRequest.status) params = params.set('status', searchRequest.status);
      if (searchRequest.name) params = params.set('name', searchRequest.name);
      if (searchRequest.startDate) params = params.set('startDate', searchRequest.startDate);
      if (searchRequest.endDate) params = params.set('endDate', searchRequest.endDate);
      if (searchRequest.createdBy) params = params.set('createdBy', searchRequest.createdBy);
      if (searchRequest.page) params = params.set('page', searchRequest.page.toString());
      if (searchRequest.pageSize) params = params.set('pageSize', searchRequest.pageSize.toString());
    }

    return this.http.get<PackageListPagedResponse>(this.apiUrl, { params });
  }

  /**
   * Obtener una lista específica por ID
   */
  getPackageList(id: number): Observable<PackageListOutModel> {
    return this.http.get<PackageListOutModel>(`${this.apiUrl}/${id}`);
  }

  /**
   * Crear una nueva lista de paquetes
   */
  createPackageList(request: CreatePackageListRequest): Observable<PackageListOutModel> {
    return this.http.post<PackageListOutModel>(this.apiUrl, request);
  }

  /**
   * Completar una lista
   */
  completeList(request: CompletePackageListRequest): Observable<PackageListOutModel> {
    return this.http.put<PackageListOutModel>(`${this.apiUrl}/${request.packageListId}/complete`, request);
  }

  // === GESTIÓN DE ITEMS ===

  /**
   * Agregar un item a una lista
   */
  addItemToList(request: AddPackageListItemRequest): Observable<PackageListItemOutModel> {
    return this.http.post<PackageListItemOutModel>(`${this.apiUrl}/${request.packageListId}/items`, request);
  }

  /**
   * Actualizar el estado de un item
   */
  updateItemStatus(listId: number, itemId: number, request: UpdatePackageListItemStatusRequest): Observable<PackageListItemOutModel> {
    return this.http.put<PackageListItemOutModel>(`${this.apiUrl}/${listId}/items/${itemId}/status`, request);
  }

  /**
   * Verificar duplicados antes de agregar un item
   */
  checkDuplicate(packageListId: number, request: CheckDuplicateItemRequest): Observable<CheckDuplicateItemResponse> {
    return this.http.post<CheckDuplicateItemResponse>(`${this.apiUrl}/${packageListId}/check-duplicate`, request);
  }

  // Eliminar una lista completa
  deleteList(packageListId: number): Observable<{message: string}> {
    return this.http.delete<{message: string}>(`${this.apiUrl}/${packageListId}`);
  }

  /**
   * Obtener paquetes únicos por tipo
   */
  getUniquePackages(type?: string, sentFrom?: string, sentTo?: string): Observable<UniquePackageModel[]> {
    let params = new HttpParams();
    if (type) {
      params = params.set('type', type);
    }
    if (sentFrom) {
      params = params.set('sentFrom', sentFrom);
    }
    if (sentTo) {
      params = params.set('sentTo', sentTo);
    }
    return this.http.get<UniquePackageModel[]>(`${this.apiUrl}/unique-packages`, { params });
  }

  /**
   * Obtener paquetes únicos que están cargados al camión
   */
  getUniquePackagesCargados(): Observable<UniquePackageModel[]> {
    return this.http.get<UniquePackageModel[]>(`${this.apiUrl}/unique-packages-cargados`);
  }

  getRepuestosCargadosAlCamion(): Observable<EmpaquetadoParaCargadoModel[]> {
    return this.http.get<EmpaquetadoParaCargadoModel[]>(`${this.apiUrl}/repuestos-cargados-al-camion`);
  }

  /**
   * Obtener paquetes únicos de una orden específica
   */
  getUniquePackagesForOrder(orderNumber: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/unique-packages/${orderNumber}`);
  }

  // Eliminar un item específico de una lista
  deleteItemFromList(packageListId: number, itemId: number): Observable<{message: string}> {
    return this.http.delete<{message: string}>(`${this.apiUrl}/${packageListId}/items/${itemId}`);
  }

  // === ESTADÍSTICAS ===

  /**
   * Obtener estadísticas de las listas
   */
  getStats(): Observable<PackageListStatsResponse> {
    return this.http.get<PackageListStatsResponse>(`${this.apiUrl}/stats`);
  }

  // POST: Limpiar inconsistencias en asignaciones de paquetes
  cleanInconsistencies(): Observable<{success: boolean, message: string, inconsistenciesFixed: number}> {
    return this.http.post<{success: boolean, message: string, inconsistenciesFixed: number}>(
      `${this.apiUrl}/clean-inconsistencies`,
      {}
    );
  }

  // === MÉTODOS ALIAS PARA COMPATIBILIDAD ===

  /**
   * Alias para getPackageLists - usado en el componente de historial
   */
  getAllLists(filters?: {
    type?: string;
    status?: string;
    name?: string;
    startDate?: Date;
    endDate?: Date;
    createdBy?: string;
    page?: number;
    pageSize?: number;
  }): Observable<PackageListPagedResponse> {
    const searchRequest: PackageListSearchRequest = {};
    
    if (filters) {
      if (filters.type) searchRequest.type = filters.type;
      if (filters.status) searchRequest.status = filters.status;
      if (filters.name) searchRequest.name = filters.name;
      if (filters.startDate) searchRequest.startDate = filters.startDate.toISOString();
      if (filters.endDate) searchRequest.endDate = filters.endDate.toISOString();
      if (filters.createdBy) searchRequest.createdBy = filters.createdBy;
      if (filters.page) searchRequest.page = filters.page;
      if (filters.pageSize) searchRequest.pageSize = filters.pageSize;
    }
    
    return this.getPackageLists(searchRequest);
  }

  /**
   * Alias para getPackageList - usado en el componente de edición
   */
  getListById(id: number): Observable<PackageListOutModel> {
    return this.getPackageList(id);
  }

  // === MÉTODOS DE CONVENIENCIA ===

  /**
   * Obtener listas activas por tipo
   */
  getActiveListsByType(type: 'empaquetado' | 'enviado' | 'cargado'): Observable<PackageListPagedResponse> {
    return this.getPackageLists({ type, status: 'active' });
  }

  /**
   * Obtener listas completadas
   */
  getCompletedLists(): Observable<PackageListPagedResponse> {
    return this.getPackageLists({ status: 'completed' });
  }

  /**
   * Obtener listas recientes (últimos 7 días)
   */
  getRecentLists(): Observable<PackageListPagedResponse> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    return this.getPackageLists({ 
      startDate: startDate.toISOString().split('T')[0] 
    });
  }

  /**
   * Verificar si un nombre de lista es único para tipo cargado
   */
  isUniqueNameForCargado(name: string): Observable<boolean> {
    return new Observable(observer => {
      this.getPackageLists({ 
        type: 'cargado', 
        status: 'active',
        name: name
      }).subscribe({
        next: (response) => {
          observer.next(response.totalCount === 0);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Verificar si un nombre de lista es único para todos los tipos
   */
  isUniqueName(name: string): Observable<boolean> {
    return new Observable(observer => {
      this.getPackageLists({ 
        status: 'active',
        name: name
      }).subscribe({
        next: (response) => {
          observer.next(response.totalCount === 0);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Obtener repuestos empaquetados para cargado automático
   */
  getEmpaquetadosParaCargado(): Observable<EmpaquetadoParaCargadoModel[]> {
    return this.http.get<EmpaquetadoParaCargadoModel[]>(`${this.apiUrl}/empaquetados-para-cargado`);
  }

  // === MÉTODOS PARA INTEGRACIÓN CON COMPONENTE EXISTENTE ===

  /**
   * Crear lista y devolver en formato compatible con el componente
   */
  createListForComponent(name: string, type: 'empaquetado' | 'enviado' | 'cargado', transportType?: 'camion' | 'camioneta'): Observable<any> {
    const request: CreatePackageListRequest = {
      name,
      type,
      transportType,
      createdBy: 'current-user' // TODO: Obtener del servicio de autenticación
    };

    return new Observable(observer => {
      this.createPackageList(request).subscribe({
        next: (response) => {
          // Convertir a formato esperado por el componente
          const componentFormat = {
            packageListId: response.packageListId,
            name: response.name,
            type: response.type,
            status: response.status,
            createdAt: response.createdAt,
            completedAt: response.completedAt,
            transportType: response.transportType,
            createdBy: response.createdBy,
            items: response.items,
            totalItems: response.totalItems,
            checkedItems: response.checkedItems,
            toLoadItems: response.toLoadItems
          };
          
          observer.next(componentFormat);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Agregar item y devolver en formato compatible con el componente
   */
  addItemForComponent(listId: number, itemData: any): Observable<any> {
    const request: AddPackageListItemRequest = {
      packageListId: listId,
      sparePartId: itemData.sparePartId,
      sparePartName: itemData.sparePartName,
      sparePartNameEs: itemData.sparePartNameEs,  // Incluir nombre en español
      sparePartNamePt: itemData.sparePartNamePt,  // Incluir nombre en portugués
      productCode: itemData.productCode,
      packageNumber: itemData.packageNumber,
      orderNumber: itemData.orderNumber,
      remitoNumber: itemData.remitoNumber
    };

    return new Observable(observer => {
      this.addItemToList(request).subscribe({
        next: (response) => {
          // Convertir a formato esperado por el componente
          const componentFormat = {
            sparePartId: response.sparePartId,
            sparePartName: response.sparePartName,
            productCode: response.productCode,
            packageNumber: response.packageNumber || '',
            scannedAt: new Date(response.scannedAt),
            orderNumber: response.orderNumber || 'N/A'
          };
          
          observer.next(componentFormat);
          observer.complete();
        },
        error: (error) => {
          // Manejar específicamente el error de duplicado
          if (error.status === 400 && error.error && error.error.isDuplicate) {
            // Crear un error personalizado con la información del duplicado
            const duplicateError = {
              isDuplicate: true,
              message: error.error.message,
              existingItemPosition: error.error.existingItemPosition,
              existingItem: error.error.existingItem
            };
            observer.error(duplicateError);
          } else {
            // Propagar otros errores tal como están
            observer.error(error);
          }
        }
      });
    });
  }

  /**
   * Escanear paquete completo
   */
  scanPackage(listId: number, packageNumber: string): Observable<ScanPackageResponse> {
    const request: ScanPackageRequest = {
      packageNumber,
      scannedBy: 'current-user' // TODO: Obtener del servicio de autenticación
    };

    return this.http.post<ScanPackageResponse>(`${this.apiUrl}/${listId}/scan-package`, request);
  }

  /**
   * Procesar paquete (cambiar estados de repuestos)
   */
  processPackage(listId: number, packageNumber: string, newStatus: string): Observable<ProcessPackageResponse> {
    const request: ProcessPackageRequest = {
      packageNumber,
      newStatus,
      processedBy: 'current-user' // TODO: Obtener del servicio de autenticación
    };

    return this.http.post<ProcessPackageResponse>(`${this.apiUrl}/${listId}/process-package`, request);
  }

  /**
   * Validar paquete para envío al taller
   */
  validatePackageForEnvio(packageNumber: string): Observable<ValidatePackageForEnvioResponse> {
    return this.http.get<ValidatePackageForEnvioResponse>(`${this.apiUrl}/validate-package-envio/${packageNumber}`);
  }

  /**
   * Validar paquete para cargado
   */
  validatePackageForCargado(packageNumber: string): Observable<ValidatePackageForCargadoResponse> {
    return this.http.get<ValidatePackageForCargadoResponse>(`${this.apiUrl}/validate-package-cargado/${packageNumber}`);
  }

  /**
   * Obtener repuestos de un paquete específico
   */
  getSparePartsByPackage(packageNumber: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/spare-parts-by-package/${packageNumber}`);
  }

  /**
   * Obtener TODOS los repuestos de una orden específica
   * Incluye el número incremental de cada repuesto en la orden
   */
  getSparePartsByOrder(orderNumber: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/spare-parts-by-order/${orderNumber}`);
  }

  // =============================================================================
  // MÉTODOS PARA GESTIÓN DE PAQUETES EXISTENTES
  // =============================================================================

  /**
   * Obtener repuestos disponibles para agregar a un paquete
   */
  getAvailableSparePartsForPackage(packageNumber: string): Observable<AvailableSparePartModel[]> {
    return this.http.get<AvailableSparePartModel[]>(`${this.apiUrl}/available-spare-parts-for-package/${packageNumber}`);
  }


  /**
   * Remover repuesto de un paquete
   */
  removeSparePartFromPackage(sparePartId: number): Observable<RemoveSparePartFromPackageResponse> {
    return this.http.delete<RemoveSparePartFromPackageResponse>(`${this.apiUrl}/remove-spare-part-from-package/${sparePartId}`);
  }

  /**
   * Eliminar paquete vacío
   */
  deleteEmptyPackage(packageNumber: string): Observable<DeleteEmptyPackageResponse> {
    return this.http.delete<DeleteEmptyPackageResponse>(`${this.apiUrl}/delete-empty-package/${packageNumber}`);
  }

  /**
   * Generar etiquetas y empaquetar orden
   */
  generateLabelsAndPackage(request: any): Observable<any> {
    return this.http.post<any>(`${environment.apiEndpoint}/order/generate-labels-and-package`, request);
  }

  /**
   * Finalizar carga al camión y actualizar estados de repuestos
   */
  finalizarCargaCamion(request: {
    tipoVehiculo: string;
    repuestosCargados: any[];
    repuestosNoCargados: any[];
    remitoNumber?: string;
  }): Observable<any> {
    // Convert to match backend DTO format
    const backendRequest: any = {
      TipoVehiculo: request.tipoVehiculo,
      RepuestosCargados: request.repuestosCargados,
      RepuestosNoCargados: request.repuestosNoCargados
    };
    
    // Incluir remitoNumber si se proporcionó
    if (request.remitoNumber) {
      backendRequest.RemitoNumber = request.remitoNumber;
    }
    
    return this.http.post<any>(`${this.apiUrl}/finalizar-carga-camion`, backendRequest);
  }

  /**
   * Generar etiqueta del paquete general (7x10 pulgadas)
   */
  generatePackageLabel(request: GeneratePackageLabelRequest): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/generate-package-label`, request, {
      responseType: 'blob'
    });
  }

  // =============================================================================
  // MÉTODOS PARA GESTIÓN DE PAQUETES (NUEVOS)
  // =============================================================================

  /**
   * Eliminar paquete completo (restaurar repuestos a su estado original)
   * Este endpoint está en OrderController
   */
  deletePackage(packageNumber: string, reason?: string, deletedBy?: string): Observable<any> {
    return this.http.delete<any>(`${environment.apiEndpoint}/order/delete-package/${packageNumber}`);
  }

  /**
   * Eliminar repuesto individual de un paquete
   */
  deleteSparePartFromPackage(packageNumber: string, sparePartId: number, reason?: string, deletedBy?: string): Observable<PackageManagementResponse> {
    return this.http.delete<PackageManagementResponse>(`${this.apiUrl}/delete-spare-part`, {
      body: {
        packageNumber,
        sparePartId,
        reason,
        deletedBy
      }
    });
  }

  /**
   * Agregar repuesto a un paquete existente
   */
  addSparePartToPackage(packageNumber: string, sparePartId: number, addedBy?: string): Observable<PackageManagementResponse> {
    return this.http.post<PackageManagementResponse>(`${this.apiUrl}/add-spare-part`, {
      packageNumber,
      sparePartId,
      addedBy
    });
  }
}

// =============================================================================
// INTERFACES PARA GESTIÓN DE PAQUETES
// =============================================================================

export interface PackageManagementResponse {
  success: boolean;
  message: string;
  affectedItems: number;
  packageDeleted: boolean;
  newPackageNumber?: string;
}

export interface DeletePackageRequest {
  packageNumber: string;
  reason?: string;
  deletedBy?: string;
}

export interface DeleteSparePartFromPackageRequest {
  packageNumber: string;
  sparePartId: number;
  reason?: string;
  deletedBy?: string;
}

export interface AddSparePartToPackageRequest {
  packageNumber: string;
  sparePartId: number;
  addedBy?: string;
}

export interface GeneratePackageLabelRequest {
  packageNumber: string;
  clienteUY?: string;
} 